import React from 'react'
import { useLocation } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'

/** Align venue ids with governance checklist / batches that used short codes (DATA_SOURCE_STRATEGY §3.3). */
function toCanonicalVenueId(venueId) {
  if (!venueId) return venueId
  const s = String(venueId).toLowerCase()
  if (s === 'diggers' || s.endsWith('diggers')) return 'diggers'
  if (s === 'leagues' || s.endsWith('leagues')) return 'leagues'
  if (s === 'golf' || s.endsWith('golf')) return 'golf'
  if (s === 'sports' || s.endsWith('sports')) return 'sports'
  return s
}

function Badge({ value }) {
  const color = value == null ? 'bg-gray-200 text-gray-500' : value <= 30 ? 'bg-green-100 text-green-800' : value <= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>{value == null ? '—' : `${value}%`}</span>
}

function Modal({ open, onClose, title, children, footer }) {
  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'unset'
    return () => { document.body.style.overflow = 'unset' }
  }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 bg-white w-[90vw] xl:w-[85vw] max-w-[90vw] xl:max-w-[85vw] rounded-lg shadow-lg">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>
        <div className="p-6 max-h-[85vh] overflow-y-auto">{children}</div>
        <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-end space-x-2">{footer}</div>
      </div>
    </div>
  )
}

function SelectField({ label, value, onChange, options, disabled }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select className="select-field" value={value ?? ''} onChange={(e)=>onChange(e.target.value)} disabled={disabled}>
        <option value="">Select</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </div>
  )
}

function SectionCard({ title, children }) {
  return (
    <div className="p-4 border rounded bg-white">
      <div className="mb-3">
        <h4 className="text-md font-semibold text-gray-900">{title}</h4>
      </div>
      {children}
    </div>
  )
}

function ReportRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline py-1 border-b border-gray-100 last:border-0 text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value || '—'}</span>
    </div>
  )
}

function ReportSection({ title, children, className = '' }) {
  return (
    <div className={`mb-3 ${className}`.trim()}>
      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{title}</h4>
      <div className="bg-gray-50 rounded p-2.5 border border-gray-100">
        {children}
      </div>
    </div>
  )
}

export default function RiskAssessment() {
  const [assessments, setAssessments] = React.useState({}) // venueId -> { operational, customerGeo, indicators, overall }
  const [openFor, setOpenFor] = React.useState(null) // venueId | null
  const [editingDraftId, setEditingDraftId] = React.useState(null) // when editing a draft, its id
  const [assessmentViewOnly, setAssessmentViewOnly] = React.useState(false) // true when viewing existing submitted assessment
  const [riskHistory, setRiskHistory] = React.useState({}) // venueId -> [{ period, scores, date }]
  const [currentPeriod, setCurrentPeriod] = React.useState(String(new Date().getFullYear()))
  const [batches, setBatches] = React.useState([]) // [{batch_id, period, reason, venues, created_at}]
  const [selectedBatch, setSelectedBatch] = React.useState(null)
  const [newBatchOpen, setNewBatchOpen] = React.useState(false)
  const [venues, setVenues] = React.useState([])
  const [venuesLoading, setVenuesLoading] = React.useState(true)
  const [newBatch, setNewBatch] = React.useState({ period: String(new Date().getFullYear()), reason: '', all: true, venues: new Set() })
  // Venue overview and history
  const [venueLatest, setVenueLatest] = React.useState({}) // venueId -> { overall, date }
  const [historyOpenFor, setHistoryOpenFor] = React.useState(null)
  const [venueHistory, setVenueHistory] = React.useState({}) // venueId -> [{ date, period, scores, reason, batch_id }]
  // Tasks panel
  const [draftAssessments, setDraftAssessments] = React.useState([])
  const [completedAssessments, setCompletedAssessments] = React.useState([])
  const [draftsError, setDraftsError] = React.useState(null) // 'fetch_failed' | null

  // Form state for modal
  const [form, setForm] = React.useState({
    reason: '',
    // Venue info
    venueLocation: '', annualRevenue: '', numEGMs: '',
    // Operational
    operatingHours: '', staffRatio: '', cctv: '', turnover: '',
    // Geographic
    crime: '', nearBorder: false, nearAirport: false, nearCasino: false, businessDistrict: false,
    // Customer
    customerBase: '', avgTxn: '', membershipCards: '',
    // ML/TF indicators
    indicators: {
      minimalGameplay: false,
      cashierTargeting: false,
      thirdPartyRequest: false,
      idReluctance: false,
      largeCash: false,
      structuring: false,
    }
  })

  const scoreParts = React.useMemo(() => {
    const n = (v) => parseInt(v || '0', 10)
    // Map selected labels to ordinal scores (1..N)
    const operationalRaw =
      Number(idx(form.operatingHours, ['<12','12-16','16-20','20-24'])) +
      Number(idx(form.staffRatio, ['High','Medium','Low','Very Low'])) +
      Number(idx(form.cctv, ['100%','80-99%','50-79%','<50%'])) +
      Number(idx(form.turnover, ['0-15%','16-30%','31-50%','50%+'])) // max 16
    const geographicRaw =
      Number(idx(form.crime, ['Low','Moderate','High','Very high'])) +
      (form.nearBorder ? 2 : 0) + (form.nearAirport ? 1 : 0) + (form.nearCasino ? 2 : 0) + (form.businessDistrict ? 1 : 0) // max 10
    const customerRaw =
      Number(idx(form.customerBase, ['Members','Mixed','Tourists','Casual/Unknown'])) +
      Number(idx(form.avgTxn, ['$1-50','$51-200','$201-500','$500+'])) +
      Number(idx(form.membershipCards, ['80%+','50-79%','20-49%','<20%'])) // max 12
    const mlTfRaw =
      (form.indicators.minimalGameplay ? 3 : 0) +
      (form.indicators.cashierTargeting ? 3 : 0) +
      (form.indicators.thirdPartyRequest ? 4 : 0) +
      (form.indicators.idReluctance ? 3 : 0) +
      (form.indicators.largeCash ? 2 : 0) +
      (form.indicators.structuring ? 4 : 0) // max 19

    // Percentages
    const operational = Math.round((operationalRaw / 16) * 100)
    const customer = Math.round((customerRaw / 12) * 100)
    const geographic = Math.round((geographicRaw / 10) * 100)
    const mlTf = Math.round((mlTfRaw / 19) * 100)
    const overall = Math.round((operational + customer + geographic + mlTf) / 4)
    return { operational, customer, geographic, mlTf, overall }
  }, [form])

  const startAssess = async (venueId, draftId = null, batchHint = null) => {
    setOpenFor(venueId)
    setEditingDraftId(draftId)
    // Pick a batch: use hint if provided, else selected batch, else find by venue
    let batch = batchHint || ((selectedBatch && Array.isArray(selectedBatch.venues) && selectedBatch.venues.includes(venueId))
      ? selectedBatch
      : (batches.find(b => Array.isArray(b.venues) && b.venues.includes(venueId)) || null))
    if (batch) { setSelectedBatch(batch); setCurrentPeriod(batch.period) }
    // Default blank form
    let nextForm = {
      reason: batch?.reason || '',
      operatingHours: '', staffRatio: '', cctv: '', turnover: '',
      crime: '', customerBase: '', avgTxn: '', membershipCards: '',
      indicators: { minimalGameplay: false, cashierTargeting: false, thirdPartyRequest: false, idReluctance: false, largeCash: false, structuring: false }
    }
    // Load latest saved details; preview only if submitted (draft + new = editable)
    let isSubmitted = false
    try {
      if (batch) {
        const r = await authFetch(getLocalApiUrl(`/api/governance/risk/assessments?batch_id=${encodeURIComponent(batch.batch_id)}&venue_id=${encodeURIComponent(venueId)}`))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          const latest = j.data[0]
          nextForm.reason = latest.reason || batch.reason || ''
          if (latest?.details && typeof latest.details === 'object') {
            nextForm = { ...nextForm, ...latest.details, reason: latest.reason || nextForm.reason, indicators: { ...nextForm.indicators, ...(latest.details.indicators||{}) } }
          }
          // Preview when submitted; draft = editable; old records (no status) = treat as submitted
          isSubmitted = latest.status !== 'draft'
          if (latest.status === 'draft') setEditingDraftId(latest.id)
        }
      }
    } catch {}
    setForm(nextForm)
    setAssessmentViewOnly(isSubmitted)
    if (isSubmitted) setEditingDraftId(null)
  }

  const saveAssess = async () => {
    setAssessments(prev => ({ ...prev, [openFor]: scoreParts }))
    const matchedBatch = batches.find(b => b.period === currentPeriod && b.venues.includes(openFor))
    setRiskHistory(prev => ({
      ...prev,
      [openFor]: [
        { period: currentPeriod, reason: (form.reason ?? matchedBatch?.reason) || '', scores: scoreParts, date: new Date().toISOString().slice(0,10) },
        ...(prev[openFor] || [])
      ]
    }))
    try {
      if (selectedBatch) {
        if (editingDraftId) {
          const dr = await authFetch(getLocalApiUrl(`/api/governance/risk/assessments/${editingDraftId}`), { method: 'DELETE' })
          const dj = await dr.json()
          if (!dj.success) throw new Error(dj.error || 'Failed to replace draft')
        }
        const user = (() => { try { const u = localStorage.getItem('paysafego_user'); return u ? JSON.parse(u) : null } catch { return null } })()
        const assessedBy = user?.user?.name || user?.name || user?.email || null
        await authFetch(getLocalApiUrl('/api/governance/risk/assessments'), {
          method:'POST', headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify({ batch_id: selectedBatch.batch_id, venue_id: openFor, period: currentPeriod, reason: form.reason ?? selectedBatch.reason ?? '', scores: scoreParts, details: form, assessed_by: assessedBy, status: 'submitted' })
        })
        // Refresh latest for this venue
        refreshVenueLatestFor(openFor)
        fetchDraftsAndCompleted()
      }
    } catch {}
    setOpenFor(null)
    setEditingDraftId(null)
  }

  const saveAsDraft = async () => {
    if (!selectedBatch) return
    try {
      if (editingDraftId) {
        const dr = await authFetch(getLocalApiUrl(`/api/governance/risk/assessments/${editingDraftId}`), { method: 'DELETE' })
        const dj = await dr.json()
        if (!dj.success) throw new Error(dj.error || 'Failed to update draft')
      }
      await authFetch(getLocalApiUrl('/api/governance/risk/assessments'), {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ batch_id: selectedBatch.batch_id, venue_id: openFor, period: currentPeriod, reason: form.reason ?? selectedBatch.reason ?? '', scores: scoreParts, details: form, status: 'draft' })
      })
      fetchDraftsAndCompleted()
      setOpenFor(null)
      setEditingDraftId(null)
    } catch {}
  }

  const deleteDraft = async (id) => {
    if (!confirm('Delete this draft assessment?')) return
    try {
      const r = await authFetch(getLocalApiUrl(`/api/governance/risk/assessments/${id}`), { method: 'DELETE' })
      const j = await r.json()
      if (j.success) {
        setDraftAssessments(prev => prev.filter(a => a.id !== id))
        fetchBatches()
      } else alert(j.error || 'Delete failed')
    } catch { alert('Delete failed') }
  }

  // Open modal from query params (venue, type, period)
  const { search } = useLocation()
  React.useEffect(() => {
    const params = new URLSearchParams(search)
    const venue = params.get('venue')
    const period = params.get('period')
    if (venue) {
      setOpenFor(venue)
    }
    if (period) setCurrentPeriod(period)
  }, [search])

  const fetchBatches = React.useCallback(async () => {
    try {
      const resp = await authFetch(getLocalApiUrl('/api/governance/risk/batches'))
      const json = await resp.json()
      if (json.success && Array.isArray(json.data)) {
        setBatches(
          json.data.map((b) => ({
            ...b,
            venues: Array.isArray(b.venues) ? b.venues.map((id) => toCanonicalVenueId(id)) : [],
          }))
        )
      }
    } catch {}
  }, [])

  React.useEffect(() => {
    fetchBatches()
  }, [fetchBatches])

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      setVenuesLoading(true)
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (cancelled) return
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          setVenues(
            j.data.map((v) => ({
              id: toCanonicalVenueId(v.venue_id),
              name: v.name || v.venue_id,
            }))
          )
        } else {
          setVenues([])
        }
      } catch {
        if (!cancelled) setVenues([])
      } finally {
        if (!cancelled) setVenuesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => {
    if (venues.length === 0) return
    setNewBatch((prev) => ({
      ...prev,
      venues: prev.all ? new Set(venues.map((v) => v.id)) : prev.venues,
    }))
  }, [venues])

  const venueName = React.useCallback((id) => venues.find((v) => v.id === id)?.name || id, [venues])

  const fetchDraftsAndCompleted = React.useCallback(async () => {
    setDraftsError(null)
    try {
      const [dRes, cRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/governance/risk/assessments?status=draft')),
        authFetch(getLocalApiUrl('/api/governance/risk/assessments?status=submitted'))
      ])
      const [dJson, cJson] = await Promise.all([dRes.json(), cRes.json()])
      if (dJson.success && dRes.ok) {
        setDraftAssessments(dJson.data || [])
      } else {
        setDraftsError('fetch_failed')
      }
      if (cJson.success) setCompletedAssessments(cJson.data || [])
    } catch (e) {
      console.error('fetchDraftsAndCompleted failed', e)
      setDraftsError('fetch_failed')
    }
  }, [])
  React.useEffect(() => { fetchDraftsAndCompleted() }, [fetchDraftsAndCompleted])

  // Build latest assessment per venue for landing overview
  React.useEffect(() => {
    if (!batches || batches.length === 0 || !venues.length) {
      setVenueLatest({})
      return
    }
    ;(async () => {
      try {
        const results = await Promise.all(venues.map(async (v) => {
          let latest = null
          for (const b of batches) {
            if (Array.isArray(b.venues) && b.venues.includes(v.id)) {
              try {
                const r = await authFetch(getLocalApiUrl(`/api/governance/risk/assessments?batch_id=${encodeURIComponent(b.batch_id)}&venue_id=${encodeURIComponent(v.id)}`))
                const j = await r.json()
                if (j.success && Array.isArray(j.data) && j.data.length > 0) {
                  const rec = j.data.find((row) => String(row.status || '').toLowerCase() !== 'draft')
                  if (!rec) continue
                  const recDate = rec?.assessed_date || rec?.date || rec?.created_at || rec?.assessed_at || ''
                  if (!latest) latest = { overall: rec?.scores?.overall || 0, date: recDate, batch_id: b.batch_id }
                }
              } catch {}
            }
          }
          return [v.id, latest]
        }))
        const map = {}
        results.forEach(([id, d]) => { if (d) map[id] = d })
        setVenueLatest(map)
      } catch {}
    })()
  }, [batches, venues])

  const refreshVenueLatestFor = async (venueId) => {
    try {
      let latest = null
      for (const b of batches) {
        if (Array.isArray(b.venues) && b.venues.includes(venueId)) {
          try {
            const r = await authFetch(getLocalApiUrl(`/api/governance/risk/assessments?batch_id=${encodeURIComponent(b.batch_id)}&venue_id=${encodeURIComponent(venueId)}`))
            const j = await r.json()
            if (j.success && Array.isArray(j.data) && j.data.length > 0) {
              const rec = j.data.find((row) => String(row.status || '').toLowerCase() !== 'draft')
              if (rec) {
                const recDate = rec?.assessed_date || rec?.date || rec?.created_at || rec?.assessed_at || ''
                if (!latest) latest = { overall: rec?.scores?.overall || 0, date: recDate, batch_id: b.batch_id }
              }
            }
          } catch {}
        }
      }
      if (latest) setVenueLatest(prev => ({ ...prev, [venueId]: latest }))
    } catch {}
  }

  const startAssessForVenue = async (venueId) => {
    let batch = batches.find(b => Array.isArray(b.venues) && b.venues.includes(venueId))
    if (!batch) {
      try {
        const period = String(new Date().getFullYear())
        const resp = await authFetch(getLocalApiUrl('/api/governance/risk/batches'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ period, reason: 'Single venue assessment', venues: [venueId] })
        })
        const json = await resp.json()
        if (json.success && json.data) {
          batch = json.data
          setBatches(prev => [batch, ...prev])
        }
      } catch {}
    }
    if (batch) {
      setSelectedBatch(batch)
      setCurrentPeriod(batch.period)
      startAssess(venueId, null, batch)
    } else {
      startAssess(venueId)
    }
  }

  const openHistory = async (venueId) => {
    setHistoryOpenFor(venueId)
    try {
      let records = []
      for (const b of batches) {
        if (Array.isArray(b.venues) && b.venues.includes(venueId)) {
          try {
            const r = await authFetch(getLocalApiUrl(`/api/governance/risk/assessments?batch_id=${encodeURIComponent(b.batch_id)}&venue_id=${encodeURIComponent(venueId)}`))
            const j = await r.json()
            if (j.success && Array.isArray(j.data)) {
              records = records.concat(j.data.map(d => {
                const date = d.assessed_date || d.date || d.created_at || d.assessed_at || ''
                return { ...d, batch_id: b.batch_id, date }
              }))
            }
          } catch {}
        }
      }
      // Simple sort by date field if present
      records.sort((a,b)=> String(b.date||'').localeCompare(String(a.date||'')))
      setVenueHistory(prev => ({ ...prev, [venueId]: records }))
    } catch {}
  }

  const createBatch = async () => {
    const chosen = newBatch.all ? venues.map((v) => v.id) : Array.from(newBatch.venues)
    if (chosen.length === 0) return
    try {
      const resp = await authFetch(getLocalApiUrl('/api/governance/risk/batches'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period: newBatch.period, reason: newBatch.reason, venues: chosen })
      })
      const json = await resp.json()
      if (json.success) {
        setBatches(prev => [json.data, ...prev])
        setNewBatchOpen(false)
      }
    } catch {}
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Venue Risk Cards — latest submitted assessment per venue */}
      <div className="card">
        <div className="p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Latest Risk Assessment by Venue</h2>
            <p className="text-sm text-gray-500">Most recent submitted assessment per venue</p>
          </div>
          <div>
            <button className="btn-primary" onClick={()=>setNewBatchOpen(true)}>New Risk Assessment</button>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {venuesLoading ? (
            <p className="text-sm text-gray-500 md:col-span-2 xl:col-span-4 py-6">Loading venues…</p>
          ) : venues.length === 0 ? (
            <p className="text-sm text-gray-500 md:col-span-2 xl:col-span-4 py-6">
              No venues from <code className="text-xs bg-gray-100 px-1 rounded">/api/venues</code>. Configure venues to run assessments.
            </p>
          ) : (
            venues.map((v) => {
            const latest = venueLatest[v.id]
            const risk = latest?.overall ?? null
            const color = risk == null ? 'bg-gray-200' : risk <= 30 ? 'bg-green-500' : risk <= 60 ? 'bg-yellow-500' : 'bg-red-500'
            return (
              <div key={v.id} className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-gray-900 mb-2">{v.name}</h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-bold">{risk != null ? `${risk}%` : '—'}</span>
                  <span className="text-xs text-gray-500">{latest?.date ? String(latest.date).slice(0,10) : 'Not assessed'}</span>
                </div>
                <div className="w-full bg-gray-200 rounded h-2 mb-3">
                  <div className={`${color} h-2 rounded`} style={{ width: `${risk || 0}%` }} />
                </div>
                <div className="flex gap-2">
                  <button className="btn-primary text-xs py-1" onClick={()=>startAssessForVenue(v.id)}>Assess Now</button>
                </div>
              </div>
            )
          })
          )}
        </div>
      </div>

      {/* Ongoing / Draft assessments */}
      <div className="card">
        <div className="p-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Ongoing Assessments (Drafts)</h2>
          <p className="text-sm text-gray-500 mb-4">Draft assessments not yet submitted — complete or delete</p>
          {draftsError ? (
            <p className="text-sm text-amber-600 py-4">
              Failed to load drafts. Check console or network tab for details.
              <button className="ml-2 btn-outline text-xs" onClick={fetchDraftsAndCompleted}>Retry</button>
            </p>
          ) : draftAssessments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500 uppercase text-xs">
                    <th className="text-left p-3">Venue</th>
                    <th className="text-left p-3">Period</th>
                    <th className="text-left p-3">Reason</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {draftAssessments.map(a => (
                    <tr key={a.id} className="border-b">
                      <td className="p-3">{venueName(a.venue_id)}</td>
                      <td className="p-3">{a.period || '—'}</td>
                      <td className="p-3">{a.reason || '—'}</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">Draft</span></td>
                      <td className="p-3 text-right space-x-2">
                        <button className="btn-outline" onClick={()=>{ const batch = batches.find(b=>b.batch_id===a.batch_id); if (batch) { setSelectedBatch(batch); setCurrentPeriod(a.period); startAssess(a.venue_id, a.id, batch) } }}>Edit</button>
                        <button className="text-red-600 hover:text-red-800 text-sm" onClick={()=>deleteDraft(a.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4">No draft assessments</p>
          )}
        </div>
      </div>

      {/* Completed assessments — grouped by year, then by venue */}
      <div className="card">
        <div className="p-4">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Completed Assessments</h2>
          <p className="text-sm text-gray-500 mb-4">Submitted assessments grouped by year, then by venue</p>
          {completedAssessments.length > 0 ? (
            (() => {
              // Group by year (period) first, then by venue_id
              const byYear = {}
              for (const a of completedAssessments) {
                const year = a.period || String(new Date().getFullYear())
                if (!byYear[year]) byYear[year] = {}
                const vid = a.venue_id || 'unknown'
                if (!byYear[year][vid]) byYear[year][vid] = []
                byYear[year][vid].push(a)
              }
              const years = Object.keys(byYear).sort((a, b) => String(b).localeCompare(String(a)))
              return (
                <div className="space-y-6">
                  {years.map(year => (
                    <div key={year}>
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">{year}</h3>
                      <div className="space-y-4">
                        {Object.entries(byYear[year])
                          .sort(([a], [b]) => venueName(a).localeCompare(venueName(b)))
                          .map(([venueId, items]) => (
                            <div key={`${year}-${venueId}`}>
                              <h4 className="text-sm font-bold text-gray-800 mb-2">{venueName(venueId)}</h4>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="border-b text-gray-500 uppercase text-xs">
                                      <th className="text-left p-3">Assessed On</th>
                                      <th className="text-left p-3">Reason</th>
                                      <th className="text-left p-3">Overall</th>
                                      <th className="text-left p-3">Assessed By</th>
                                      <th className="text-right p-3">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map(a => (
                                      <tr key={a.id} className="border-b">
                                        <td className="p-3">{(a.assessed_date || a.date || '').toString().slice(0,10) || '—'}</td>
                                        <td className="p-3">{a.reason || '—'}</td>
                                        <td className="p-3"><Badge value={a?.scores?.overall} /></td>
                                        <td className="p-3">{a.assessed_by || '—'}</td>
                                        <td className="p-3 text-right">
                                          <button type="button" className="text-psg-teal-600 hover:text-psg-teal-800 hover:underline font-medium" onClick={()=>{ const batch = batches.find(b=>b.batch_id===a.batch_id); if (batch) { setSelectedBatch(batch); setCurrentPeriod(a.period); startAssess(a.venue_id) } }}>View details</button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            })()
          ) : (
            <p className="text-sm text-gray-500 py-4">No completed assessments</p>
          )}
        </div>
      </div>

      <Modal
        open={!!openFor}
        onClose={()=>setOpenFor(null)}
        title={`Risk Assessment — ${venueName(openFor)}`}
        footer={(
          <>
            <button className="btn-outline" onClick={()=>{ setOpenFor(null); setEditingDraftId(null) }}>{assessmentViewOnly ? 'Close' : 'Cancel'}</button>
            {!assessmentViewOnly && (
              <>
                <button className="btn-outline" onClick={saveAsDraft}>Save as draft</button>
                <button className="btn-primary" onClick={saveAssess}>Save Assessment</button>
              </>
            )}
          </>
        )}
      >
        {assessmentViewOnly ? (
          /* Submitted assessment — preview only, PDF-friendly report layout */
          <article className="risk-assessment-report print:shadow-none print:border print:border-gray-300" style={{ breakInside: 'avoid' }}>
            {/* 1. Title & period */}
            <header className="border-b border-gray-200 pb-3 mb-3">
              <h2 className="text-lg font-semibold text-gray-900">{venueName(openFor)}</h2>
              <p className="text-xs text-gray-500 mt-0.5">Period: {currentPeriod || '—'}</p>
            </header>

            {/* 2. Reason */}
            <section className="mb-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reason for assessment</h3>
              <p className="text-sm text-gray-900">{form.reason || '—'}</p>
            </section>

            {/* 3. Overall risk score */}
            <section className="mb-4 py-3 px-4 bg-gray-50 rounded border border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Overall risk</h3>
              <p className="text-2xl font-bold m-0">
                {scoreParts.overall != null ? (
                  <span className={`px-3 py-1 rounded ${scoreParts.overall <= 30 ? 'bg-green-100 text-green-800' : scoreParts.overall <= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                    {scoreParts.overall}%
                  </span>
                ) : '—'}
              </p>
            </section>

            {/* 4. Split scores */}
            <section className="mb-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Score breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded border text-xs">
                  <span className="text-gray-500">Operational</span><Badge value={scoreParts.operational} />
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded border text-xs">
                  <span className="text-gray-500">Customer</span><Badge value={scoreParts.customer} />
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded border text-xs">
                  <span className="text-gray-500">Geographic</span><Badge value={scoreParts.geographic} />
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded border text-xs">
                  <span className="text-gray-500">ML/TF</span><Badge value={scoreParts.mlTf} />
                </div>
              </div>
            </section>

            {/* 5. What contributed — always visible, PDF-friendly */}
            <section className="border-t border-gray-200 pt-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">What contributed</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0">
                <ReportSection title="Venue Info">
                  <ReportRow label="Location" value={form.venueLocation} />
                  <ReportRow label="Annual GGR ($)" value={form.annualRevenue} />
                  <ReportRow label="EGMs" value={form.numEGMs} />
                </ReportSection>
                <ReportSection title="Operational">
                  <ReportRow label="Hours/day" value={form.operatingHours} />
                  <ReportRow label="Staff ratio" value={form.staffRatio} />
                  <ReportRow label="CCTV" value={form.cctv} />
                  <ReportRow label="Turnover" value={form.turnover} />
                </ReportSection>
                <ReportSection title="Customer">
                  <ReportRow label="Customer base" value={form.customerBase} />
                  <ReportRow label="Avg txn" value={form.avgTxn} />
                  <ReportRow label="Membership" value={form.membershipCards} />
                </ReportSection>
                <ReportSection title="Geographic">
                  <ReportRow label="Crime area" value={form.crime} />
                  <div className="pt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                    {[['nearBorder','Border'],['nearAirport','Airport'],['nearCasino','Casino'],['businessDistrict','Business']].map(([k,l]) => (
                      <span key={k} className={form[k] ? 'text-green-600' : 'text-gray-400'}>{l}: {form[k] ? '✓' : '—'}</span>
                    ))}
                  </div>
                </ReportSection>
                <ReportSection title="ML/TF Indicators" className="md:col-span-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                    {[['minimalGameplay','Minimal gameplay'],['cashierTargeting','Cashier targeting'],['thirdPartyRequest','3rd party'],['idReluctance','ID reluctance'],['largeCash','Large cash'],['structuring','Structuring']].map(([k,l]) => (
                      <span key={k} className={form.indicators?.[k] ? 'text-amber-700 font-medium' : 'text-gray-400'}>{l}: {form.indicators?.[k] ? '✓' : '—'}</span>
                    ))}
                  </div>
                </ReportSection>
              </div>
            </section>
          </article>
        ) : (
          /* Editable form for new assessments */
          <>
            <div className="mb-4 flex items-center gap-3">
              <label className="text-sm text-gray-600">Assessment Period</label>
              <input className="input-field w-32" value={currentPeriod ?? ''} onChange={(e)=>setCurrentPeriod(e.target.value)} placeholder="YYYY" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for assessment</label>
              <textarea className="input-field w-full min-h-[80px]" value={form.reason ?? ''} onChange={(e)=>setForm(p=>({ ...p, reason: e.target.value }))} placeholder="e.g., Annual review, Incident follow-up, Regulatory requirement" rows={3} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-5">
                <SectionCard title="Venue Information">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location / Suburb</label>
                      <input className="input-field" value={form.venueLocation ?? ''} onChange={(e)=>setForm(p=>({ ...p, venueLocation: e.target.value }))} placeholder="Enter location" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annual GGR ($)</label>
                      <input type="number" className="input-field" value={form.annualRevenue ?? ''} onChange={(e)=>setForm(p=>({ ...p, annualRevenue: e.target.value }))} placeholder="e.g. 2500000" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Licensed EGMs</label>
                      <input type="number" className="input-field" value={form.numEGMs ?? ''} onChange={(e)=>setForm(p=>({ ...p, numEGMs: e.target.value }))} placeholder="e.g. 120" />
                    </div>
                  </div>
                </SectionCard>
                <SectionCard title="Operational Factors">
                  <div className="space-y-3">
                    <SelectField label="Operating hours per day" value={form.operatingHours} onChange={(v)=>setForm(p=>({ ...p, operatingHours: v }))} options={['<12','12-16','16-20','20-24']} />
                    <SelectField label="Staff to customer ratio" value={form.staffRatio} onChange={(v)=>setForm(p=>({ ...p, staffRatio: v }))} options={['High','Medium','Low','Very Low']} />
                    <SelectField label="CCTV coverage" value={form.cctv} onChange={(v)=>setForm(p=>({ ...p, cctv: v }))} options={['100%','80-99%','50-79%','<50%']} />
                    <SelectField label="Annual staff turnover" value={form.turnover} onChange={(v)=>setForm(p=>({ ...p, turnover: v }))} options={['0-15%','16-30%','31-50%','50%+']} />
                  </div>
                </SectionCard>
                <SectionCard title="Customer Profile">
                  <div className="space-y-3">
                    <SelectField label="Primary customer base" value={form.customerBase} onChange={(v)=>setForm(p=>({ ...p, customerBase: v }))} options={['Members','Mixed','Tourists','Casual/Unknown']} />
                    <SelectField label="Average transaction size" value={form.avgTxn} onChange={(v)=>setForm(p=>({ ...p, avgTxn: v }))} options={['$1-50','$51-200','$201-500','$500+']} />
                    <SelectField label="Membership/Loyalty card usage" value={form.membershipCards} onChange={(v)=>setForm(p=>({ ...p, membershipCards: v }))} options={['80%+','50-79%','20-49%','<20%']} />
                  </div>
                </SectionCard>
                <SectionCard title="Geographic & Environmental">
                  <div className="space-y-3">
                    <SelectField label="Crime area" value={form.crime} onChange={(v)=>setForm(p=>({ ...p, crime: v }))} options={['Low','Moderate','High','Very high']} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[['nearBorder','Near international border'],['nearAirport','Near major airport'],['nearCasino','Near casinos'],['businessDistrict','Business/financial district']].map(([k,label]) => (
                        <label key={k} className="flex items-center space-x-2 text-sm">
                          <input type="checkbox" className="h-4 w-4" checked={!!form[k]} onChange={() => setForm(prev=>({ ...prev, [k]: !prev[k] }))} />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </SectionCard>
                <SectionCard title="ML/TF Risk Indicators">
                  <div className="space-y-2">
                    {[['minimalGameplay','Cash to credit with minimal gameplay'],['cashierTargeting','Customers targeting specific cashiers'],['thirdPartyRequest','Third-party cheque requests'],['idReluctance','Reluctance to provide ID'],['largeCash','Large cash amounts despite ATM'],['structuring','Regular amounts under thresholds']].map(([k,label]) => (
                      <label key={k} className="flex items-center space-x-2 text-sm">
                        <input type="checkbox" className="h-4 w-4" checked={!!form.indicators[k]} onChange={() => setForm(prev=>({ ...prev, indicators: { ...prev.indicators, [k]: !prev.indicators[k] } }))} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </SectionCard>
              </div>
              <div className="xl:col-span-1">
                <div className="p-4 border rounded bg-gray-50 sticky top-4 space-y-4">
                  <div>
                    <h4 className="text-md font-semibold text-gray-900">Risk Scores</h4>
                    <p className="text-xs text-gray-600 mt-1">Live as you edit</p>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-white rounded border">
                      <div className="text-xs text-gray-500">Operational</div>
                      <div className="text-xl font-semibold"><Badge value={scoreParts.operational} /></div>
                    </div>
                    <div className="p-3 bg-white rounded border">
                      <div className="text-xs text-gray-500">Customer</div>
                      <div className="text-xl font-semibold"><Badge value={scoreParts.customer} /></div>
                    </div>
                    <div className="p-3 bg-white rounded border">
                      <div className="text-xs text-gray-500">Geographic</div>
                      <div className="text-xl font-semibold"><Badge value={scoreParts.geographic} /></div>
                    </div>
                    <div className="p-3 bg-white rounded border">
                      <div className="text-xs text-gray-500">Overall</div>
                      <div className="text-2xl font-bold"><Badge value={scoreParts.overall} /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* History Modal */}
      <Modal
        open={!!historyOpenFor}
        onClose={()=>setHistoryOpenFor(null)}
        title={`Risk History — ${venueName(historyOpenFor)}`}
        footer={<button className="btn-outline" onClick={()=>setHistoryOpenFor(null)}>Close</button>}
      >
        {historyOpenFor && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500 uppercase text-xs">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Period</th>
                  <th className="text-left p-3">Overall</th>
                  <th className="text-left p-3">Batch</th>
                </tr>
              </thead>
              <tbody>
                {(venueHistory[historyOpenFor]||[]).map((r,idx)=> (
                  <tr key={idx} className="border-b">
                    <td className="p-3">{String(r.date||'').slice(0,10)}</td>
                    <td className="p-3">{r.period||'—'}</td>
                    <td className="p-3">{r?.scores?.overall ?? '—'}%</td>
                    <td className="p-3">{r.batch_id||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* New Batch Modal */}
      <Modal
        open={newBatchOpen}
        onClose={()=>setNewBatchOpen(false)}
        title="Create Risk Assessment Batch"
        footer={(
          <>
            <button className="btn-outline" onClick={()=>setNewBatchOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={createBatch}>Create</button>
          </>
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Assessment Period (YYYY or label)</label>
            <input className="input-field" value={newBatch.period ?? ''} onChange={(e)=>setNewBatch(p=>({ ...p, period: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Reason</label>
            <input className="input-field" placeholder="e.g., Officer change, Mid‑year review" value={newBatch.reason ?? ''} onChange={(e)=>setNewBatch(p=>({ ...p, reason: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-2">Scope</label>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input type="checkbox" className="h-4 w-4" checked={newBatch.all} onChange={(e)=>setNewBatch(p=>({ ...p, all: e.target.checked, venues: e.target.checked ? new Set(venues.map(v=>v.id)) : new Set() }))} />
              <span>All venues</span>
            </label>
            {!newBatch.all && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {venues.length === 0 ? (
                  <p className="text-sm text-gray-500 col-span-2">No venues loaded yet.</p>
                ) : (
                venues.map(v => (
                  <label key={v.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="h-4 w-4" checked={newBatch.venues.has(v.id)} onChange={(e)=>setNewBatch(p=>{
                      const set = new Set(p.venues)
                      if (e.target.checked) set.add(v.id); else set.delete(v.id)
                      return { ...p, venues: set }
                    })} />
                    <span>{v.name}</span>
                  </label>
                ))
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )

  function idx(value, arr) {
    const i = arr.indexOf(value)
    return i >= 0 ? String(i + 1) : ''
  }
}

